/**
 * Поля ввода.
 *
 * Мобильная клавиатура — половина успеха формы: у суммы она числовая,
 * у телефона — телефонная. Ошибка живёт рядом с полем, а не всплывает
 * тостом, который уезжает раньше, чем человек его прочитал.
 */

import clsx from 'clsx'
import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react'

import { haptic } from '@/shared/telegram'

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label?: string
  hint?: ReactNode
  error?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      {label && (
        <span className="text-text-muted mb-1.5 block px-1 text-caption font-medium">{label}</span>
      )}
      {children}
      {error ? (
        <span className="text-danger mt-1.5 block px-1 text-caption">{error}</span>
      ) : hint ? (
        <span className="text-text-muted mt-1.5 block px-1 text-caption leading-snug">{hint}</span>
      ) : null}
    </label>
  )
}

// Поле — вдавленное в бумагу, а не наклеенное на неё: внутренняя тень сверху
// плюс тёплая заливка. В фокусе появляется мягкое шалфейное кольцо.
const INPUT_CLASS =
  'bg-surface-muted/60 border-line w-full rounded-[var(--radius-button)] border px-4 py-3 ' +
  'text-body outline-none placeholder:text-text-muted ' +
  'shadow-[inset_0_1px_2px_rgba(72,56,38,0.05)] ' +
  'focus:border-accent focus:bg-surface focus:ring-4 focus:ring-[color-mix(in_srgb,var(--color-accent)_14%,transparent)] ' +
  'transition-[background-color,border-color,box-shadow] duration-200'

export function TextInput({
  className,
  invalid,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return (
    <input
      className={clsx(INPUT_CLASS, invalid && 'border-danger', className)}
      // 16px и выше — иначе iOS зумит страницу при фокусе
      {...rest}
    />
  )
}

export function TextArea({
  className,
  invalid,
  rows = 4,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      rows={rows}
      className={clsx(INPUT_CLASS, 'resize-none leading-snug', invalid && 'border-danger', className)}
      {...rest}
    />
  )
}

/**
 * Ввод суммы. Значение хранится строкой: приводить к number ради поля,
 * а потом обратно — верный способ потерять копейки.
 */
export function MoneyInput({
  value,
  onValueChange,
  currency = '₽',
  suffix,
  invalid,
  placeholder = '0',
}: {
  value: string
  onValueChange: (next: string) => void
  currency?: string
  suffix?: string
  invalid?: boolean
  placeholder?: string
}) {
  return (
    <div
      className={clsx(
        'bg-surface-muted/60 border-line flex items-center rounded-[var(--radius-button)] border px-4',
        'shadow-[inset_0_1px_2px_rgba(72,56,38,0.05)]',
        'focus-within:border-accent focus-within:bg-surface transition-colors duration-200',
        invalid && 'border-danger',
      )}
    >
      <input
        inputMode="decimal"
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          const next = event.target.value.replace(/[^\d.,]/g, '').replace(',', '.')
          // две цифры после точки максимум — больше в деньгах не бывает
          if (/^\d{0,9}(\.\d{0,2})?$/.test(next)) onValueChange(next)
        }}
        className="tabular font-display min-w-0 flex-1 bg-transparent py-3 text-title outline-none"
      />
      <span className="text-text-muted ml-2 shrink-0 text-body">
        {currency}
        {suffix && <span className="ml-1">{suffix}</span>}
      </span>
    </div>
  )
}

export interface SegmentedOption<T extends string> {
  value: T
  label: string
  hint?: string
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: SegmentedOption<T>[]
  value: T
  onChange: (next: T) => void
}) {
  return (
    <div className="bg-surface-muted flex gap-1 rounded-[var(--radius-button)] p-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => {
            haptic('select')
            onChange(option.value)
          }}
          className={clsx(
            // высота не меньше 44px: это тач-таргет, а не декоративная вкладка
            'min-h-[44px] flex-1 rounded-[var(--radius-thumb)] px-2 py-2 text-note font-medium transition-colors',
            option.value === value
              ? 'bg-surface text-text border-line/70 border shadow-[var(--shadow-card)]'
              : 'text-text-muted',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: ReactNode
  description?: string
  disabled?: boolean
}) {
  const id = useId()
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="block text-body font-medium">
          {label}
        </label>
        {description && (
          <p className="text-text-muted mt-0.5 text-caption leading-snug">{description}</p>
        )}
      </div>
      {/* Тумблер выглядит как системный (51×31), но нажимается по площади 51×44:
          видимый размер и зона нажатия — разные вещи. */}
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => {
          haptic('select')
          onChange(!checked)
        }}
        className={clsx(
          'flex h-11 w-[51px] shrink-0 items-center',
          disabled && 'opacity-50',
        )}
      >
        <span
          className={clsx(
            'relative block h-[31px] w-[51px] rounded-full transition-colors duration-200',
            checked ? 'bg-accent' : 'bg-line',
          )}
        >
          {/* left-[2px] обязателен. Без явной позиции кружок встаёт туда, где
              оказался бы в потоке, а кнопка по умолчанию центрирует текст —
              кружок уезжал к середине дорожки и во включённом состоянии
              вылезал за край. Ход: 51 − 27 − 2·2 = 20px. */}
          <span
            className={clsx(
              'bg-surface absolute top-[2px] left-[2px] size-[27px] rounded-full shadow-sm',
              'transition-transform duration-200 ease-[var(--ease-out-soft)]',
              checked ? 'translate-x-[20px]' : 'translate-x-0',
            )}
          />
        </span>
      </button>
    </div>
  )
}
